import { constant } from '../draft/feedback-messenger-window-draft.constant'
import {
	DraftScope,
	DraftType,
	loadDraft,
	removeDraft,
	setIntent,
} from '../draft/feedback-messenger-window-draft.storage'

const boundMarkerAttribute = 'data-clm-draft-select-bound'

const getButtonScope = (button: HTMLButtonElement): DraftScope | null => {
	const scope = button.getAttribute(constant.selector.scopeAttribute)
	const type = button.getAttribute(constant.selector.typeAttribute)
	if (scope === null || (type !== 'email' && type !== 'sms')) {
		return null
	}
	return { scope, type: type as DraftType }
}

const formatSavedAt = (savedAt: number) =>
	new Intl.DateTimeFormat(window.appConfig.lang, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(savedAt))

const getDiscardButton = (form: HTMLFormElement) =>
	form.querySelector<HTMLButtonElement>(`[${constant.selector.discardButton}]`)

// Visibility is re-evaluated on every init pass (each nette.ajax complete):
// a draft autosaved after the initial bind must still reveal the buttons.
const refreshButton = (button: HTMLButtonElement) => {
	const form = button.closest('form')
	const scope = getButtonScope(button)
	if (form === null || scope === null) {
		return
	}

	const draft = loadDraft(scope)
	const discardButton = getDiscardButton(form)

	if (draft === null) {
		button.hidden = true
		if (discardButton !== null) {
			discardButton.hidden = true
		}
		return
	}

	const timeElement = button.querySelector(`[${constant.selector.continueTime}]`)
	if (timeElement !== null) {
		timeElement.textContent = `(${formatSavedAt(draft.savedAt)})`
	}
	button.hidden = false
	if (discardButton !== null) {
		discardButton.hidden = false
	}
}

const bindButton = (button: HTMLButtonElement) => {
	if (button.getAttribute(boundMarkerAttribute) !== null) {
		return
	}
	button.setAttribute(boundMarkerAttribute, 'true')

	const form = button.closest('form')
	const scope = getButtonScope(button)
	if (form === null || scope === null) {
		return
	}

	// nette.ajax submits this form from its own click handler on the submit
	// button and prevents the native submit, so no `submit` event ever fires —
	// the intent has to be recorded on the clicks themselves.
	const submitter = form.querySelector<HTMLInputElement | HTMLButtonElement>(
		'input[type="submit"], button[type="submit"]',
	)
	submitter?.addEventListener('click', () => setIntent(scope, 'fresh'))

	getDiscardButton(form)?.addEventListener('click', () => {
		removeDraft(scope)
		refreshButton(button)
	})

	button.addEventListener('click', () => {
		// Read the draft at click time — it may have changed since the bind.
		const draft = loadDraft(scope)
		if (draft === null) {
			refreshButton(button)
			return
		}
		const templateSelect = form.querySelector<HTMLSelectElement>(constant.selector.templateSelect)
		if (templateSelect !== null && draft.templateId !== null) {
			const draftOption = templateSelect.querySelector(`option[value="${draft.templateId}"]`)
			if (draftOption !== null) {
				templateSelect.value = String(draft.templateId)
			}
		}
		setIntent(scope, 'restore')
		form.requestSubmit(submitter ?? undefined)
	})
}

export const FeedbackMessengerTemplateSelect = (() => {
	const init = () => {
		document.querySelectorAll<HTMLButtonElement>(`[${constant.selector.continueButton}]`).forEach((button) => {
			bindButton(button)
			refreshButton(button)
		})
	}

	return {
		init,
	}
})()
